import {
  Flex,
  HStack,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuDivider,
  Portal,
  useDisclosure,
  useToast,
  Avatar,
  Icon,
  Text,
} from '@chakra-ui/react';
import { FaEllipsisV, FaEdit, FaTrash, FaShare, FaFolder, FaFolderMinus, FaRegClock, FaPlus } from 'react-icons/fa';
import { RiShareLine, RiWifiLine, RiWifiOffLine } from 'react-icons/ri';
import { MdOutlineSmartToy } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { HelivanColor } from '../helivan/HelivanColor';
import {
  CustomAgent,
  useDeleteCustomAgentMutation,
  useRemoveAgentFromCollectionMutation,
} from '../../generated/graphql';
import { EditAgentModal } from './EditAgentModal';
import { ShareAgentModal } from './ShareAgentModal';
import { AddToCollectionModal } from './AddToCollectionModal';
import { CreateNewProjectModal } from '../Projects/CreateNewProjectModal';
import { GatewayHealthTag } from './GatewayHealthTag';

interface MyAgentCardProps {
  agent: CustomAgent;
  onUpdate: () => void;
  collectionId?: string;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const MyAgentCard = ({ agent, onUpdate, collectionId }: MyAgentCardProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const editModal = useDisclosure();
  const shareModal = useDisclosure();
  const addToCollectionModal = useDisclosure();
  const createProjectModal = useDisclosure();

  const [deleteAgent] = useDeleteCustomAgentMutation();
  const [removeFromCollection] = useRemoveAgentFromCollectionMutation();

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    try {
      await deleteAgent({
        variables: { id: agent.id },
      });
      onUpdate();
      toast({
        title: 'Agent deleted',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete agent',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleCardClick = () => {
    navigate(`/agents/${agent.id}`);
  };

  const handleRemoveFromCollection = async () => {
    if (!collectionId) return;
    try {
      await removeFromCollection({
        variables: {
          collectionId,
          agentId: agent.id,
        },
      });
      onUpdate();
      toast({
        title: 'Removed from collection',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove from collection',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <>
      <EditAgentModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        agent={agent}
        onSuccess={onUpdate}
      />
      <ShareAgentModal
        isOpen={shareModal.isOpen}
        onClose={shareModal.onClose}
        agent={agent}
        onSuccess={onUpdate}
      />
      <AddToCollectionModal
        isOpen={addToCollectionModal.isOpen}
        onClose={addToCollectionModal.onClose}
        agentId={agent.id}
        agentName={agent.name}
      />
      <CreateNewProjectModal
        isOpen={createProjectModal.isOpen}
        onClose={createProjectModal.onClose}
        hasTokens={true}
        preSelectedCustomAgentId={agent.id}
        preSelectedCustomAgentName={agent.name}
      />

      <Flex
        width="full"
        height="100%"
        minWidth="240px"
        maxWidth="300px"
        flexDirection="column"
        bg="white"
        borderRadius="12px"
        overflow="hidden"
        boxShadow="0 1px 3px rgba(0,0,0,0.06)"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        _hover={{
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,71,125,0.1)',
          borderColor: HelivanColor.text.helivanBlue,
        }}
        transition="all 0.15s ease"
        cursor="pointer"
        onClick={handleCardClick}
      >
        {/* Header with type icon and visibility */}
        <Flex
          px="14px"
          py="10px"
          bg={HelivanColor.background.secondary}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor={HelivanColor.border.primary}
        >
          <HStack spacing={2}>
            <Icon
              as={MdOutlineSmartToy}
              width="14px"
              height="14px"
              color={HelivanColor.text.helivanBlue}
            />
            <Text fontSize="11px" fontWeight={500} color={HelivanColor.text.blueMid}>
              Agent
            </Text>
          </HStack>
          <HStack spacing={2}>
            <FaRegClock size={10} color={HelivanColor.text.blueMid} />
            <Text fontSize="10px" color={HelivanColor.text.blueMid}>
              {formatDate(agent.createdAt)}
            </Text>
          </HStack>
        </Flex>

        <Flex flexDirection="column" p="14px" gap="10px" flex="1">
          {/* Avatar, Title and Actions */}
          <Flex width="full" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <HStack spacing={3} flex="1" alignItems="flex-start">
              <Avatar
                size="sm"
                name={agent.name}
                src={agent.avatarUrl ?? undefined}
                bg={HelivanColor.text.helivanBlue}
                color="white"
              />
              <Flex direction="column" flex="1" minWidth={0}>
                <Text
                  fontSize="15px"
                  fontWeight={600}
                  color={HelivanColor.text.blueDark}
                  lineHeight="1.3"
                  noOfLines={1}
                >
                  {agent.name}
                </Text>
                {!agent.isOwner && agent.owner && (
                  <Text fontSize="10px" color={HelivanColor.text.blueMid} mt="2px">
                    by {agent.owner.fullName}
                  </Text>
                )}
              </Flex>
            </HStack>

            <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
              <Menu isLazy strategy="fixed" placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  icon={<FaEllipsisV size={12} />}
                  size="xs"
                  variant="ghost"
                  minW="28px"
                  h="28px"
                  borderRadius="6px"
                  _hover={{ bg: HelivanColor.background.secondary }}
                />
                <Portal>
                  <MenuList
                    fontSize="13px"
                    onClick={(e) => e.stopPropagation()}
                    zIndex={1400}
                    boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor={HelivanColor.border.primary}
                    py={2}
                    minW="180px"
                  >
                    <MenuItem
                      icon={<FaPlus size={12} />}
                      onClick={() => createProjectModal.onOpen()}
                      py={2}
                      px={3}
                      _hover={{ bg: HelivanColor.background.secondary }}
                    >
                      Create Project
                    </MenuItem>
                    <MenuDivider my={1} borderColor={HelivanColor.border.primary} />
                    {agent.canEdit && (
                      <MenuItem
                        icon={<FaEdit size={12} />}
                        onClick={() => editModal.onOpen()}
                        py={2}
                        px={3}
                        _hover={{ bg: HelivanColor.background.secondary }}
                      >
                        Edit
                      </MenuItem>
                    )}
                    {agent.isOwner && (
                      <MenuItem
                        icon={<FaShare size={12} />}
                        onClick={() => shareModal.onOpen()}
                        py={2}
                        px={3}
                        _hover={{ bg: HelivanColor.background.secondary }}
                      >
                        Share
                      </MenuItem>
                    )}
                    {collectionId ? (
                      <MenuItem
                        icon={<FaFolderMinus size={12} />}
                        onClick={handleRemoveFromCollection}
                        py={2}
                        px={3}
                        _hover={{ bg: HelivanColor.background.secondary }}
                      >
                        Remove from Collection
                      </MenuItem>
                    ) : (
                      <MenuItem
                        icon={<FaFolder size={12} />}
                        onClick={() => addToCollectionModal.onOpen()}
                        py={2}
                        px={3}
                        _hover={{ bg: HelivanColor.background.secondary }}
                      >
                        Add to Collection
                      </MenuItem>
                    )}
                    {agent.isOwner && (
                      <>
                        <MenuDivider my={1} borderColor={HelivanColor.border.primary} />
                        <MenuItem
                          icon={<FaTrash size={12} />}
                          color="red.500"
                          onClick={handleDelete}
                          py={2}
                          px={3}
                          _hover={{ bg: 'red.50' }}
                        >
                          Delete
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </Portal>
              </Menu>
            </HStack>
          </Flex>

          {/* Description */}
          {agent.description && (
            <Text
              fontSize="12px"
              color={HelivanColor.text.blueMid}
              lineHeight="1.4"
              noOfLines={2}
            >
              {agent.description}
            </Text>
          )}

          {/* Status and Stats */}
          <Flex gap="6px" flexWrap="wrap" alignItems="center">
            {/* Online/Offline status badge */}
            {(() => {
              const isOnline = agent.connectionType === 'CONNECTOR'
                ? agent.connectorStatus === 'ONLINE'
                : agent.status === 'ACTIVE';
              return (
                <HStack
                  spacing={1}
                  px="6px"
                  py="2px"
                  borderRadius="4px"
                  bg={isOnline ? 'rgba(34, 197, 94, 0.1)' : 'rgba(100, 116, 139, 0.1)'}
                  border="1px solid"
                  borderColor={isOnline ? 'rgba(34, 197, 94, 0.3)' : 'rgba(100, 116, 139, 0.2)'}
                >
                  {isOnline ? (
                    <RiWifiLine size={10} color="#22c55e" />
                  ) : (
                    <RiWifiOffLine size={10} color="#64748b" />
                  )}
                  <Text
                    fontSize="10px"
                    color={isOnline ? '#22c55e' : '#64748b'}
                    fontWeight={500}
                  >
                    {isOnline ? 'Online' : 'Offline'}
                  </Text>
                </HStack>
              );
            })()}
            {/* Local-gateway health (only relevant once the cloud link is ONLINE) */}
            {agent.connectionType === 'CONNECTOR' && agent.connectorStatus === 'ONLINE' && (
              <GatewayHealthTag
                connectionType={agent.connectionType}
                gatewayHealth={agent.gatewayHealth}
                gatewayHealthReason={agent.gatewayHealthReason}
              />
            )}
            {agent.lastConnectionTestAt && agent.connectionType !== 'CONNECTOR' && (
              <Text fontSize="10px" color={HelivanColor.text.blueMid}>
                Tested {formatRelativeTime(agent.lastConnectionTestAt)}
              </Text>
            )}
            {agent.lastConnectorPingAt && agent.connectionType === 'CONNECTOR' && (
              <Text fontSize="10px" color={HelivanColor.text.blueMid}>
                Last ping {formatRelativeTime(agent.lastConnectorPingAt)}
              </Text>
            )}
            {agent.shareCount > 0 && (
              <HStack
                spacing={1}
                px="6px"
                py="2px"
                borderRadius="4px"
                bg={HelivanColor.background.secondary}
              >
                <RiShareLine size={10} color={HelivanColor.text.blueMid} />
                <Text fontSize="10px" color={HelivanColor.text.blueMid}>
                  {agent.shareCount}
                </Text>
              </HStack>
            )}
          </Flex>

          {/* Primary CTA: create a watch scoped to this single agent.
              Pinned to the bottom (mt="auto") so it aligns across cards. */}
          <Button
            leftIcon={<FaPlus size={12} />}
            mt="auto"
            w="full"
            h="36px"
            bgGradient="linear(135deg, #00477D 0%, #1a5a94 100%)"
            color="white"
            borderRadius="10px"
            fontSize="13px"
            fontWeight={600}
            boxShadow="0 2px 8px rgba(0,71,125,0.25)"
            _hover={{
              bgGradient: 'linear(135deg, #003a66 0%, #14507f 100%)',
              boxShadow: '0 4px 12px rgba(0,71,125,0.35)',
              transform: 'translateY(-1px)',
            }}
            _active={{ transform: 'scale(0.99)' }}
            transition="all 0.15s ease"
            onClick={(e) => {
              e.stopPropagation();
              createProjectModal.onOpen();
            }}
          >
            Create watch
          </Button>
        </Flex>
      </Flex>
    </>
  );
};
